import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import PurchaseCard from '@componentsV2/PurchaseCard';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  fetchPropertyOwner as apiFetchPropertyOwner,
  updatePropertyOwner as apiUpdatePropertyOwner,
  fetchConsents as apiFetchConsents,
  updateConsents as apiUpdateConsents,
  deletePropertyOwner as apiDeletePropertyOwner,
} from '../../../../../api/propertyowners';
import ErrorContainer from '../../../../../components/ErrorContainer';
import PageLayout from '../../../../../components/layouts/WithHeader';
import ContentLoader from '../../../../../components/loaders/ContentLoader';
import { getPropertyOwnerFullName } from '../../../../../utils/propertyowners';
import {
  MARKETINGEMAIL,
  PROPERTYVALUEESTIMATEUPDATES,
} from '../../../../../constants';
import Link from '../../../../../components/atomic/Link';
import {
  boligmappaPlusCancel,
  getUserTransactions,
} from '../../../../../api/boligmappaPlus';
import {
  getActiveSubscriptions,
  getFormattedTransactionsData,
  getPaidSinglePayments,
} from '../../../../../utils/boligmappaPlus';
import useSearchQuery from '../../../../../hooks/useSearchQuery';
import useDesktop from '../../../../../hooks/useDesktop';
import useFetchEffect from '../../../../../hooks/useFetchEffect';
import useDashboardLinkPrefix from '../../../../../hooks/useDashboardLinkPrefix';
import matomo from '../../../../../utils/matomo';
import { getResponseErrorMessage } from '../../../../../utils/requests';

export const defaultConsents = [
  {
    isGranted: false,
    type: MARKETINGEMAIL,
  },
];

export const getConsentByType = (consents, type) =>
  (consents || []).find((consent) => consent.type === type);

const Account = () => {
  const history = useHistory();
  const isDesktop = useDesktop();
  const { cancelled } = useSearchQuery();
  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [userTransactions, setUserTransactions] = useState({});
  const [activeCancelId, setActiveCancelId] = useState();
  const { toast } = useToast();
  const [editedEmail, setEditedEmail] = useState(null);
  const [editedMobile, setEditedMobile] = useState(null);

  const {
    fetching,
    fetchingError,
    data: propertyOwner,
    setData: setPropertyOwner,
  } = useFetchEffect({
    initialDataState: {},
    defaultDataState: {},
    initialFetchingState: true,
    apiFetchFunction: apiFetchPropertyOwner,
  });

  const { data: consents, setData: setConsents } = useFetchEffect({
    initialFetchingState: true,
    initialDataState: defaultConsents,
    defaultDataState: defaultConsents,
    apiFetchFunction: apiFetchConsents,
  });

  const propertyOwnerName = getPropertyOwnerFullName(propertyOwner);

  const { email, mobile } = propertyOwner || {};

  const onCancel = (id) => {
    setActiveCancelId(id);
  };

  const setEmail = (value) => {
    setEditedEmail(value);
  };

  const setMobile = (value) => {
    setEditedMobile(value);
  };

  const fetchUserTransactions = async () => {
    const [error, response] = await getUserTransactions();
    if (!error && response) {
      setUserTransactions(response.responses);
    }
  };

  useEffect(() => {
    setEditedEmail(email);
    setEditedMobile(mobile);
  }, [propertyOwner]);

  const fetchStoredUserTransactions = () => {
    if (userTransactions) {
      const transactionsData = [
        ...getActiveSubscriptions(userTransactions.subscriptions),
        ...getPaidSinglePayments(userTransactions.singlePayments),
      ];

      setTransactions(getFormattedTransactionsData(transactionsData));
      setLoading(false);
    }
  };

  const onSubscriptionCancel = async (subId) => {
    const [error, response] = await boligmappaPlusCancel(subId);
    if (!error && response) {
      setTransactions((prevState) =>
        prevState.filter((el) => el.orderNumber !== subId)
      );
      history.push(`/subscription-cancelled?redirect=${dashboardLinkPrefix}`);
      matomo.clickEvent({
        category: 'Boligmappa+',
        action: 'Subscription Cancelled Successfully via request',
      });
    } else {
      toast({
        title: 'Noe gikk galt.',
        variant: 'destructive',
      });
    }
  };

  const onCancelConfirm = () => {
    onSubscriptionCancel(activeCancelId);
  };

  useEffect(() => {
    fetchUserTransactions();
    if (cancelled) {
      toast({
        title: 'Abonnementet er avsluttet!',
        variant: 'success',
      });
      history.replace({ search: '' });
    }
  }, []);

  useEffect(() => {
    fetchStoredUserTransactions();
  }, [userTransactions]);

  const onFormSubmit = async () => {
    setRequestInProgress(true);
    let data;
    if (editedEmail !== email) {
      data = { ...data, email: editedEmail };
    }
    if (editedMobile !== mobile) {
      data = { ...data, mobile: editedMobile };
    }
    const [error, response] = await apiUpdatePropertyOwner(data);
    if (error && !response) {
      toast({
        title: getResponseErrorMessage(error),
        variant: 'destructive',
      });
    } else {
      setPropertyOwner({
        ...propertyOwner,
        mobile: editedMobile,
        email: editedEmail,
      });

      toast({
        variant: 'success',
      });
    }
    setRequestInProgress(false);
  };

  const onConsentsSubmit = async (newConsents) => {
    setRequestInProgress(true);

    const [errorConsent, consentResponse] = await apiUpdateConsents(
      newConsents
    );

    if (errorConsent && !consentResponse) {
      toast({
        title: getResponseErrorMessage(errorConsent),
        variant: 'destructive',
      });
    } else {
      toast({
        variant: 'success',
      });
    }
    setRequestInProgress(false);
  };

  const onDeleteProfileSubmit = async () => {
    const [error, response] = await apiDeletePropertyOwner();

    if (error && !response) {
      toast({
        title: getResponseErrorMessage(error),
        variant: 'destructive',
      });
    } else {
      toast({
        defaultTitle: false,
        description: `Takk for at du valgte Boligmappa!
        Hvis du vil bruke Boligmappas tjenester igjen, finner du oss på www.boligmappa.`,
        type: 'success',
        variant: 'success',
      });

      setTimeout(() => {
        history.push('/logout');
      }, 3000);
    }
  };

  const onConsentsChange = (event, type) => {
    let newConsents;
    if (consents.find((consent) => consent.type === type)) {
      newConsents = consents.map((consent) =>
        type === consent.type ? { ...consent, isGranted: event } : consent
      );
    } else {
      newConsents = [
        ...consents,
        {
          type,
          isGranted: event,
        },
      ];
    }
    setConsents(newConsents);
    onConsentsSubmit(newConsents);
  };

  return (
    <PageLayout minFullHeight headerTitle={!isDesktop ? 'Min Profil' : ''}>
      {fetchingError ? (
        <ErrorContainer paddingTop={false} errorResponse={fetchingError} />
      ) : (
        <>
          {fetching ? (
            <>
              <div className="flex flex-col gap-4">
                <ContentLoader style={{ margin: '27px 0' }} height={200} />
                <ContentLoader style={{ margin: '27px 0' }} height={200} />
              </div>
              <ContentLoader style={{ margin: '27px 0' }} height={200} />
              <ContentLoader style={{ margin: '27px 0' }} height={200} />
            </>
          ) : (
            <>
              {/* Name Card */}
              <div className="flex flex-col gap-6 font-normal">
                <Card>
                  <CardHeader>
                    <CardTitle>Ditt navn</CardTitle>
                    <CardDescription>
                      Personopplysninger hentes gjennom folkeregisteret og kan
                      ikke endres av Boligmappa.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input
                      disabled
                      type="text"
                      placeholder="Name"
                      value={propertyOwnerName || ''}
                    />
                  </CardContent>
                </Card>

                {/* Contact Info Card */}
                <Card>
                  <CardHeader>
                    <CardTitle>Kontaktinformasjon</CardTitle>
                    <CardDescription>
                      For å gi deg den beste opplevelsen med Boligmappa,
                      anbefaler vi at du legger igjen både e-post og
                      telefonnummer. Dette gir oss flere muligheter til å
                      kontakte deg når det er viktig informasjon eller
                      oppdateringer om boligen din.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="grid items-center basis-1/2 sm:basis-full gap-2">
                        <Label htmlFor="email">Epostadresse</Label>
                        <Input
                          type="email"
                          id="email"
                          placeholder="Epostadresse"
                          value={editedEmail || ''}
                          disabled={fetching || requestInProgress}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="grid items-center basis-1/2 sm:basis-full gap-2">
                        <Label htmlFor="tel">Telefonnummer</Label>
                        <Input
                          type="tel"
                          id="tel"
                          placeholder="Skriv inn ditt telefonnummer"
                          value={editedMobile || ''}
                          disabled={fetching || requestInProgress}
                          onChange={(e) => setMobile(e.target.value)}
                        />
                      </div>
                    </div>
                  </CardContent>
                  <Separator />
                  <CardFooter>
                    <div className="flex flex-row w-full justify-end">
                      <Button
                        disabled={
                          !(editedEmail !== email || editedMobile !== mobile)
                        }
                        className="cursor-pointer"
                        variant="destructive"
                        onClick={onFormSubmit}
                      >
                        Lagre
                      </Button>
                    </div>
                  </CardFooter>
                </Card>

                {/* Consent Cards */}
                <div className="flex lg:flex-row flex-col gap-5 w-fill">
                  <Card
                    style={{
                      width: '-webkit-fill-available',
                    }}
                  >
                    <CardHeader>
                      <CardTitle>Vil du ha gode boligtips?</CardTitle>
                      <CardDescription>
                        Økonomi, oppussing og hjelp til bedre boligvalg. Gjør
                        som over 150 000 og abonnér på Boligmappas
                      </CardDescription>
                    </CardHeader>

                    <CardFooter>
                      <div className="flex flex-row gap-x-5">
                        <Switch
                          className="cursor-pointer"
                          onCheckedChange={(e) =>
                            onConsentsChange(e, MARKETINGEMAIL)
                          }
                          checked={
                            (
                              getConsentByType(consents, MARKETINGEMAIL) || {
                                isGranted: false,
                              }
                            ).isGranted
                          }
                        />
                        <div className="flex flex-col">
                          <p className="font-medium">Ja takk - meld meg på</p>
                        </div>
                      </div>
                    </CardFooter>
                  </Card>

                  <Card style={{ width: '-webkit-fill-available' }}>
                    <CardHeader>
                      <CardTitle>Verdiestimat på din bolig</CardTitle>
                      <CardDescription>
                        Jeg ønsker å motta månedlig e-post med oppdatert
                        prisestimat for eiendommene mine.
                      </CardDescription>
                    </CardHeader>

                    <CardFooter>
                      <div className="flex flex-row gap-x-5">
                        <Switch
                          className="cursor-pointer"
                          onCheckedChange={(e) =>
                            onConsentsChange(e, PROPERTYVALUEESTIMATEUPDATES)
                          }
                          checked={
                            (
                              getConsentByType(
                                consents,
                                PROPERTYVALUEESTIMATEUPDATES
                              ) || {
                                isGranted: false,
                              }
                            ).isGranted
                          }
                        />
                        <div className="flex flex-col">
                          <p className="font-medium">
                            Jeg ønsker å få pris-oppdateringer på boligen min!
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Kun tilgjengelig med Boligmappa+
                          </p>
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                </div>

                {/* Purchasing info Cards  */}
                {!loading &&
                  transactions.length > 0 &&
                  transactions.map((el) => (
                    <PurchaseCard
                      key={el.orderNumber}
                      type={el.type}
                      dashboardLinkPrefix={dashboardLinkPrefix}
                      date={el.createdDate}
                      orderNumber={el.orderNumber}
                      amount={el.amount}
                      nextBillingDate={el.nextBillingDate}
                      cancelled={el.cancelled}
                      onSubCancel={() => onCancel(el.orderNumber)}
                      onCancelConfirm={() => onCancelConfirm()}
                    />
                  ))}

                {/* Delete Profile Card  */}
                <Card>
                  <CardHeader>
                    <CardTitle>Slett kontoen din</CardTitle>
                    <CardDescription className="pb-6">
                      Du eier dataen tilknyttet din Boligmappe-bruker, selv om
                      du velger å slette den.
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="bg-red-50 px-6 py-4">
                    <div className="flex flex-row items-center justify-between w-full">
                      <p>
                        Ja, jeg forstår at jeg er ansvarlig for eventuelle
                        konsekvenser av at jeg sletter kontoen min.
                      </p>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="destructive"
                            className="cursor-pointer"
                          >
                            Slett konto
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>
                              Er du helt sikker på at du vil slette kontoen din?
                            </DialogTitle>
                            <DialogDescription>
                              <div className=" flex flex-col gap-4">
                                Sletting er permanent, og kan ikke
                                tilbakestilles.
                                <div className="flex flex-row items-center justify-end gap-2">
                                  <DialogClose asChild>
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      className="cursor-pointer"
                                    >
                                      Avbryt
                                    </Button>
                                  </DialogClose>
                                  <DialogClose asChild>
                                    <Button
                                      variant="destructive"
                                      onClick={() => onDeleteProfileSubmit()}
                                      className="cursor-pointer"
                                    >
                                      Slett konto
                                    </Button>
                                  </DialogClose>
                                </div>
                              </div>
                            </DialogDescription>
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardFooter>
                </Card>

                {/* Link Div  */}
                <div className="flex flex-col items-center gap-4 pt-8 pb-20">
                  <Link
                    isExternal
                    openInNewTab
                    href="https://www.boligmappa.no/vilkar-for-kjop"
                  >
                    <p className="cursor-pointer">
                      Vilkår for kjøp (boligmappa.no)
                    </p>
                  </Link>
                  <Link
                    isExternal
                    openInNewTab
                    href="https://www.boligmappa.no/personvern"
                    className="cursor-pointer"
                  >
                    <p>Brukervilkår og personvern</p>
                  </Link>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default Account;
